import React from 'react';
import { BillData, PaymentRecord } from '../types/billing';

interface Props {
  companyId: string;
  companyName: string;
  bills: BillData[];
  payments: PaymentRecord[];
  onClose: () => void;
}

const CompanyDetailsModal: React.FC<Props> = ({ companyId, companyName, bills, payments, onClose }) => {
  const companyBills = bills.filter(b => b.clientId === companyId);
  const companyPayments = payments.filter(p => p.companyId === companyId);
  const totalBilled = companyBills.reduce((s, b) => s + b.totalAmount, 0);
  const totalPaid = companyPayments.reduce((s, p) => s + p.amount, 0);
  const outstanding = totalBilled - totalPaid;

  const currency = (n: number) => {
    const rounded = Math.round(n);
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(rounded);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-5xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-900">{companyName} — Financial Summary</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white border rounded p-3">
            <div className="text-sm text-gray-500">Total Billed</div>
            <div className="text-xl font-semibold">{currency(totalBilled)}</div>
          </div>
          <div className="bg-white border rounded p-3">
            <div className="text-sm text-gray-500">Total Paid</div>
            <div className="text-xl font-semibold">{currency(totalPaid)}</div>
          </div>
          <div className="bg-white border rounded p-3">
            <div className="text-sm text-gray-500">Outstanding</div>
            <div className={`text-xl font-semibold ${outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>{currency(outstanding)}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-semibold mb-2">Bills</h4>
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companyBills.map(b => (
                    <tr key={b.id}>
                      <td className="px-4 py-2 text-sm">{b.invoiceNumber}</td>
                      <td className="px-4 py-2 text-sm">{new Date(b.invoiceDate).toLocaleDateString('en-GB')}</td>
                      <td className="px-4 py-2 text-sm text-right">{currency(b.totalAmount)}</td>
                      <td className="px-4 py-2 text-sm">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Payments</h4>
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ref</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companyPayments.map(p => (
                    <tr key={p.id}>
                      <td className="px-4 py-2 text-sm">{new Date(p.date).toLocaleDateString('en-GB')}</td>
                      <td className="px-4 py-2 text-sm">{p.mode}</td>
                      <td className="px-4 py-2 text-sm">{p.reference || '-'}</td>
                      <td className="px-4 py-2 text-sm text-right">{currency(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="mt-4 text-right">
          <button onClick={onClose} className="px-4 py-2 bg-avis-blue text-white rounded-md hover:bg-avis-dark">Close</button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailsModal;


